import {
    cn,
    isStandalone,
    isTerminalMessage,
    PromoWrapper,
    syncPromoReactUIConfigurationStore,
    syncPromoReactUINavigationStore,
    syncPromoReactUISessionStore,
    updateReactUIIsSoundMuted,
    useMessageActions,
} from '@amusnet/react-ui'
import { useAmuseBoostPromoWidgetPositions } from '@hooks/useAmuseBoostPromoWidgetPositions'
import useGetBetAmount from '@hooks/useGetBetAmount'
import { useComponentStatesStore } from '@stores/componentStatesStore'
import { useConfigurationStore } from '@stores/configurationStore'
import { useLuckySpinsStore } from '@stores/luckySpinsStore'
import { useNavigationStore } from '@stores/navigationStore'
import { usePlayerInfoStore } from '@stores/playerInfoStore'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import React, { useEffect, useState } from 'react'

interface PromoProps {
    stageScale: number
}

const Promo: React.FC<PromoProps> = ({ stageScale }) => {
    const setCurrentAvailablePromoWidgets = useComponentStatesStore((state) => state.setCurrentAvailablePromoWidgets)

    const [shouldPromoMoveToTopLayer, setShouldPromoMoveToTopLayer] = useState(false)
    const isGambleActive = useSpinButtonSectionStore((state) => state.isGambleActive)

    const isGameLoaded = useConfigurationStore((state) => state.isGameLoaded)
    const viewport = useConfigurationStore((state) => state.viewport)
    const configurationStoreState = {
        viewport,
        stageDimensions: useConfigurationStore((state) => state.stageDimensions),
        currency: useConfigurationStore((state) => state.currency),
        currencyHideCents: useConfigurationStore((state) => state.currencyHideCents),
        playerName: useConfigurationStore((state) => state.playerName),
        playerUid: useConfigurationStore((state) => state.playerUid),
        authToken: useConfigurationStore((state) => state.token),
        apiBaseUrl: useConfigurationStore((state) => state.apiBaseUrl),
        language: useConfigurationStore((state) => state.language),
        portalUid: useConfigurationStore((state) => state.portalUid),
    }

    useEffect(() => {
        syncPromoReactUIConfigurationStore(configurationStoreState)
    }, [configurationStoreState])

    const isSoundMuted = useConfigurationStore((state) => state.isSoundMuted)

    useEffect(() => {
        updateReactUIIsSoundMuted(isSoundMuted)
    }, [isSoundMuted])

    const betAmount = useGetBetAmount()
    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)
    const updateBalance = usePlayerInfoStore((state) => state.updateBalance)
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

    useEffect(() => {
        syncPromoReactUISessionStore({ betAmount, balance: displayedBalance })
    }, [betAmount, displayedBalance])

    useMessageActions({
        showSystemMessage({ messageType }) {
            if (isTerminalMessage(messageType)) {
                syncPromoReactUISessionStore({ isSessionActive: false })
            }
        },
    })

    const isHomeDisabled = useNavigationStore((state) => state.isHomeDisabled)

    useEffect(() => {
        syncPromoReactUINavigationStore({ isHomeDisabled })
    }, [isHomeDisabled])

    const isLSAvailableInGame = useConfigurationStore((state) => state.hasLuckySpins)
    const isCurrentlyConsumingLS = useLuckySpinsStore((state) => state.showRemainingSpinsBanner)

    const widgetComponentDimensions = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').dimensions)
    const widgetComponentStyles = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').styles)
    const { promoWidgetPosition } = useAmuseBoostPromoWidgetPositions()

    const setConfiguration = useConfigurationStore((state) => state.setConfiguration)
    const shouldDisableSpaceKey = useConfigurationStore((state) => state.shouldDisableSpaceKey)
    const [initialShouldDisableSpaceKey] = useState(shouldDisableSpaceKey)

    const setShouldDisableBetShortcut = (shouldDisable: boolean) => {
        if (shouldDisable) {
            setConfiguration({ shouldDisableSpaceKey: shouldDisable })
        } else {
            setConfiguration({ shouldDisableSpaceKey: initialShouldDisableSpaceKey })
        }
    }

    const handleUpdateBalance = (newBalance: number) => {
        if (newBalance === balance) return

        if (isStandalone) {
            window.postMessage(
                {
                    id: 'EventForGame',
                    data: {
                        command: 'balance',
                        balance: newBalance,
                    },
                },
                '*'
            )
        } else {
            updateBalance(newBalance)
        }
    }

    return (
        <div
            className={cn(
                'absolute inset-0 w-full h-full pointer-events-none',
                shouldPromoMoveToTopLayer ? 'z-[1001]' : 'z-[201]',
                // hide when in gamble unless widget forces to be seen eg. when bomb explodes
                isGambleActive && !shouldPromoMoveToTopLayer && 'opacity-0'
            )}
        >
            <PromoWrapper
                stageScale={stageScale}
                widgetComponentConfig={{
                    styles: {
                        position: 'absolute',
                        ...widgetComponentDimensions,
                        ...promoWidgetPosition,
                    },
                    classNames: widgetComponentStyles,
                }}
                isGameLoaded={isGameLoaded}
                isLSAvailableInGame={isLSAvailableInGame}
                isCurrentlyConsumingLS={isCurrentlyConsumingLS}
                updateBalance={handleUpdateBalance}
                setShouldPromoMoveToTopLayer={setShouldPromoMoveToTopLayer}
                setCurrentAvailableWidgets={setCurrentAvailablePromoWidgets}
                setShouldDisableBetShortcut={setShouldDisableBetShortcut}
            />
        </div>
    )
}

export default Promo
